import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  AppBar,
  Toolbar,
  Typography,
} from "@mui/material";
import { useHistory, useLocation, Switch, Route } from "react-router-dom";
import {
  Group,
  School,
  Title,
  Workspaces,
  Menu as MenuIcon,
  HowToReg,
  Rule,
  Money,
  MilitaryTech,
  Groups,
  DirectionsCar,
  Build,
  Assignment,
  Settings,
  CheckCircle,
  Report,
  Category,
  Search,
  CarRepair,
  Engineering,
} from "@mui/icons-material";
import CodeTable from "./CodeTable";
import { codePaths } from "../../utils/enums";
import { PATH_CODE_SETTING } from "../../utils/routeConstants";
import { getAccessPermission, getUserAccessPermission } from "../../reducers/PermissionsReducer";

const drawerWidth = 240;
const collapsedDrawerWidth = 60;
const path = PATH_CODE_SETTING;

// always add new menu item in alphabetical order
const dynamicMenuItems = [
  {
    label: "Applicant Status",
    path: codePaths.APPLICANT_STATUS,
    icon: <HowToReg />,
    permission: { area: "codes", key: "Applicant Status" },
  },
  {
    label: "Applicant Test Result",
    path: codePaths.APPLICANT_TEST_RESULT,
    icon: <Rule />,
    permission: { area: "codes", key: "Applicant Test Result" },
  },
  {
    label: "Department/Division",
    path: codePaths.DEPARTMENT,
    icon: <Group />,
    permission: { area: "codes", key: "Department" },
  },
  {
    label: "Education",
    path: codePaths.EDUCATION,
    icon: <School />,
    permission: { area: "codes", key: "Education" },
  },
  {
    label: "Employment Status",
    path: codePaths.EMPLOYMENT_STATUS,
    icon: <Rule />,
    permission: { area: "codes", key: "Employment Status" },
  },
  {
    label: "Rank",
    path: codePaths.RANK,
    icon: <MilitaryTech />,
    permission: { area: "codes", key: "Rank" },
  },
  {
    label: "Relationship",
    path: codePaths.Relationship,
    icon: <Groups />,
    permission: { area: "codes", key: "Relationship" },
  },
  {
    label: "Section",
    path: codePaths.SECTION,
    icon: <Workspaces />,
    permission: { area: "codes", key: "Section" },
  },
  {
    label: "Title/Position",
    path: codePaths.TITLE,
    icon: <Title />,
    permission: { area: "codes", key: "Title" },
  },
  {
    label: "Training Cost",
    path: codePaths.TRAINING_COST,
    icon: <Money />,
    permission: { area: "codes", key: "Training Cost" },
  },
  // Fleet Codes
  {
    label: "Vehicle Status",
    path: codePaths.VehicleStatus,
    icon: <DirectionsCar />,
    permission: { area: "codes", key: "Vehicle Statuses" },
  },
  {
    label: "Work Order Types",
    path: codePaths.WorkOrderTypes,
    icon: <Build />,
    permission: { area: "codes", key: "Work Order Types" },
  },
  {
    label: "Work Order Statuses",
    path: codePaths.WorkOrderStatus,
    icon: <Assignment />,
    permission: { area: "codes", key: "Work Order Statuses" },
  },
  {
    label: "Equipment Types",
    path: codePaths.EquipmentTypes,
    icon: <Settings />,
    permission: { area: "codes", key: "Equipment Types" },
  },
  {
    label: "Equipment Statuses",
    path: codePaths.EquipmentStatus,
    icon: <CheckCircle />,
    permission: { area: "codes", key: "Equipment Statuses" },
  },
  {
    label: "Equipment Conditions",
    path: codePaths.EquipmentConditions,
    icon: <Report />,
    permission: { area: "codes", key: "Equipment Conditions" },
  },
  {
    label: "Equipment Category",
    path: codePaths.EquipmentCategory,
    icon: <Category />,
    permission: { area: "codes", key: "Equipment Category Codes" },
  },
  {
    label: "Inspection Types",
    path: codePaths.InspectionTypes,
    icon: <Search />,
    permission: { area: "codes", key: "Inspection Types" },
  },
  {
    label: "Vehicle Service Requirements",
    path: codePaths.VehicleServiceRequirements,
    icon: <CarRepair />,
    permission: { area: "codes", key: "Vehicle Service Requirements" },
  },
  {
    label: "Equipment Service Requirements",
    path: codePaths.EquipmentServiceRequirements,
    icon: <Engineering />,
    permission: { area: "codes", key: "Equipment Service Requirements" },
  },
];

const codeRoutes = [
  {
    path: codePaths.APPLICANT_STATUS,
    serviceName: "code-per-applicant-status",
    stateName: "applicantStatusCodes",
    tableName: "Applicant Status Codes",
    columnOrderKey: "applicantStatusCodeTableColumnOrder",
    columnSizeKey: "applicantStatusCodeTableColumnSizes",
    columnVisibilityKey: "applicantStatusCodeTableColumnVisibility",
    tableSortModelKey: "applicantStatusCodeTableSortModel",
    title: "Applicant Status",
    isAMSCode: true,
  },
  {
    path: codePaths.APPLICANT_TEST_RESULT,
    serviceName: "code-per-applicant-test-result",
    stateName: "applicantTestResultCodes",
    tableName: "Applicant Test Result Codes",
    columnOrderKey: "applicantTestResultCodeTableColumnOrder",
    columnSizeKey: "applicantTestResultCodeTableColumnSizes",
    columnVisibilityKey: "applicantTestResultCodeTableColumnVisibility",
    tableSortModelKey: "applicantTestResultCodeTableSortModel",
    title: "Applicant Test Result",
    isAMSCode: true,
  },
  {
    path: codePaths.EDUCATION,
    serviceName: "code-per-education",
    stateName: "educationCodes",
    tableName: "Education Codes",
    columnOrderKey: "educationCodeTableColumnOrder",
    columnSizeKey: "educationCodeTableColumnSizes",
    columnVisibilityKey: "educationCodeTableColumnVisibility",
    tableSortModelKey: "educationCodeTableSortModel",
    title: "Education",
    isAMSCode: true,
  },
  {
    path: codePaths.DEPARTMENT,
    serviceName: "code-per-department",
    stateName: "departmentCodes",
    tableName: "Department Codes",
    columnOrderKey: "departmentCodeTableColumnOrder",
    columnSizeKey: "departmentCodeTableColumnSizes",
    columnVisibilityKey: "departmentCodeTableColumnVisibility",
    tableSortModelKey: "departmentCodeTableSortModel",
    title: "Department/Division",
    isAMSCode: null,
  },
  {
    path: codePaths.SECTION,
    serviceName: "code-per-section",
    stateName: "sectionCodes",
    tableName: "Section Codes",
    columnOrderKey: "sectionCodeTableColumnOrder",
    columnSizeKey: "sectionCodeTableColumnSizes",
    columnVisibilityKey: "sectionCodeTableColumnVisibility",
    tableSortModelKey: "sectionCodeTableSortModel",
    title: "Section",
    isAMSCode: true,
  },
  {
    path: codePaths.TITLE,
    serviceName: "code-per-title-position",
    stateName: "titlePositionCodes",
    tableName: "Title/Position Codes",
    columnOrderKey: "titlePositionCodeTableColumnOrder",
    columnSizeKey: "titlePositionCodeTableColumnSizes",
    columnVisibilityKey: "titlePositionCodeTableColumnVisibility",
    tableSortModelKey: "titlePositionCodeTableSortModel",
    title: "Title/Position",
    isAMSCode: true,
  },
  {
    path: codePaths.EMPLOYMENT_STATUS,
    serviceName: "code-per-employment-status",
    stateName: "employmentStatusCodes",
    tableName: "Employment Status Codes",
    columnOrderKey: "employmentStatusCodeTableColumnOrder",
    columnSizeKey: "employmentStatusCodeTableColumnSizes",
    columnVisibilityKey: "employmentStatusCodeTableColumnVisibility",
    tableSortModelKey: "employmentStatusCodeTableSortModel",
    title: "Employment Status",
    isAMSCode: true,
  },
  {
    path: codePaths.TRAINING_COST,
    serviceName: "code-per-training-cost",
    stateName: "trainingCostCodes",
    tableName: "Training Cost Codes",
    columnOrderKey: "trainingCostCodeTableColumnOrder",
    columnSizeKey: "trainingCostCodeTableColumnSizes",
    columnVisibilityKey: "trainingCostCodeTableColumnVisibility",
    tableSortModelKey: "trainingCostCodeTableSortModel",
    title: "Training Cost",
    isAMSCode: true,
  },
  {
    path: codePaths.RANK,
    serviceName: "code-per-rank",
    stateName: "rankCodes",
    tableName: "Rank Codes",
    columnOrderKey: "rankCodeTableColumnOrder",
    columnSizeKey: "rankCodeTableColumnSizes",
    columnVisibilityKey: "rankCodeTableColumnVisibility",
    tableSortModelKey: "rankCodeTableSortModel",
    title: "Rank",
    isAMSCode: null,
  },
  {
    path: codePaths.Relationship,
    serviceName: "code-per-relationships",
    stateName: "relationshipsCodes",
    tableName: "Relationships Codes",
    columnOrderKey: "relationshipsCodeTableColumnOrder",
    columnSizeKey: "relationshipsCodeTableColumnSizes",
    columnVisibilityKey: "relationshipsCodeTableColumnVisibility",
    tableSortModelKey: "relationshipsCodeTableSortModel",
    title: "Relationship",
    isAMSCode: true,
  },
  // Fleet Codes
  {
    path: codePaths.VehicleStatus,
    serviceName: "code-per-vehicle-status",
    stateName: "vehicleStatusCodes",
    tableName: "Vehicle Status Codes",
    columnOrderKey: "vehicleStatusCodeTableColumnOrder",
    columnSizeKey: "vehicleStatusCodeTableColumnSizes",
    columnVisibilityKey: "vehicleStatusCodeTableColumnVisibility",
    tableSortModelKey: "vehicleStatusCodeTableSortModel",
    title: "Vehicle Status",
    isAMSCode: false,
  },
  {
    path: codePaths.WorkOrderTypes,
    serviceName: "code-per-work-order-types",
    stateName: "workOrderTypesCodes",
    tableName: "Work Order Types Codes",
    columnOrderKey: "workOrderTypesCodeTableColumnOrder",
    columnSizeKey: "workOrderTypesCodeTableColumnSizes",
    columnVisibilityKey: "workOrderTypesCodeTableColumnVisibility",
    tableSortModelKey: "workOrderTypesCodeTableSortModel",
    title: "Work Order Types",
    isAMSCode: false,
  },
  {
    path: codePaths.WorkOrderStatus,
    serviceName: "code-per-work-order-status",
    stateName: "workOrderStatusCodes",
    tableName: "Work Order Status Codes",
    columnOrderKey: "workOrderStatusCodeTableColumnOrder",
    columnSizeKey: "workOrderStatusCodeTableColumnSizes",
    columnVisibilityKey: "workOrderStatusCodeTableColumnVisibility",
    tableSortModelKey: "workOrderStatusCodeTableSortModel",
    title: "Work Order Status",
    isAMSCode: false,
  },
  {
    path: codePaths.EquipmentTypes,
    serviceName: "code-per-equipment-types",
    stateName: "equipmentTypesCodes",
    tableName: "Equipment Types Codes",
    columnOrderKey: "equipmentTypesCodeTableColumnOrder",
    columnSizeKey: "equipmentTypesCodeTableColumnSizes",
    columnVisibilityKey: "equipmentTypesCodeTableColumnVisibility",
    tableSortModelKey: "equipmentTypesCodeTableSortModel",
    title: "Equipment Types",
    isAMSCode: false,
  },
  {
    path: codePaths.EquipmentStatus,
    serviceName: "code-per-equipment-status",
    stateName: "equipmentStatusCodes",
    tableName: "Equipment Status Codes",
    columnOrderKey: "equipmentStatusCodeTableColumnOrder",
    columnSizeKey: "equipmentStatusCodeTableColumnSizes",
    columnVisibilityKey: "equipmentStatusCodeTableColumnVisibility",
    tableSortModelKey: "equipmentStatusCodeTableSortModel",
    title: "Equipment Status",
    isAMSCode: false,
  },
  {
    path: codePaths.EquipmentConditions,
    serviceName: "code-per-equipment-conditions",
    stateName: "equipmentConditionsCodes",
    tableName: "Equipment Conditions Codes",
    columnOrderKey: "equipmentConditionsCodeTableColumnOrder",
    columnSizeKey: "equipmentConditionsCodeTableColumnSizes",
    columnVisibilityKey: "equipmentConditionsCodeTableColumnVisibility",
    tableSortModelKey: "equipmentConditionsCodeTableSortModel",
    title: "Equipment Conditions",
    isAMSCode: false,
  },
  {
    path: codePaths.EquipmentCategory,
    serviceName: "code-per-equipment-category",
    stateName: "equipmentCategoryCodes",
    tableName: "Equipment Category Codes",
    columnOrderKey: "equipmentCategoryCodeTableColumnOrder",
    columnSizeKey: "equipmentCategoryCodeTableColumnSizes",
    columnVisibilityKey: "equipmentCategoryCodeTableColumnVisibility",
    tableSortModelKey: "equipmentCategoryCodeTableSortModel",
    title: "Equipment Category",
    isAMSCode: false,
  },
  {
    path: codePaths.InspectionTypes,
    serviceName: "code-per-inspection-types",
    stateName: "inspectionTypesCodes",
    tableName: "Inspection Types Codes",
    columnOrderKey: "inspectionTypesCodeTableColumnOrder",
    columnSizeKey: "inspectionTypesCodeTableColumnSizes",
    columnVisibilityKey: "inspectionTypesCodeTableColumnVisibility",
    tableSortModelKey: "inspectionTypesCodeTableSortModel",
    title: "Inspection Types",
    isAMSCode: false,
  },
  {
    path: codePaths.VehicleServiceRequirements,
    serviceName: "code-per-vehicle-service-requirements",
    stateName: "vehicleServiceRequirementsCodes",
    tableName: "Vehicle Service Requirements Codes",
    columnOrderKey: "vehicleServiceRequirementsCodeTableColumnOrder",
    columnSizeKey: "vehicleServiceRequirementsCodeTableColumnSizes",
    columnVisibilityKey: "vehicleServiceRequirementsCodeTableColumnVisibility",
    tableSortModelKey: "vehicleServiceRequirementsCodeTableSortModel",
    title: "Vehicle Service Requirements",
    isAMSCode: false,
  },
  {
    path: codePaths.EquipmentServiceRequirements,
    serviceName: "code-per-equipment-service-requirements",
    stateName: "equipmentServiceRequirementsCodes",
    tableName: "Equipment Service Requirements Codes",
    columnOrderKey: "equipmentServiceRequirementsCodeTableColumnOrder",
    columnSizeKey: "equipmentServiceRequirementsCodeTableColumnSizes",
    columnVisibilityKey: "equipmentServiceRequirementsCodeTableColumnVisibility",
    tableSortModelKey: "equipmentServiceRequirementsCodeTableSortModel",
    title: "Equipment Service Requirements",
    isAMSCode: false,
  },
];

const Codes = () => {
  const history = useHistory();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDrawerCollapsed, setIsDrawerCollapsed] = useState(false);
  const userAgency = useSelector((s) => s.user.userAgency);
  const user = useSelector((state) => state.user);
  const amsPermission =
    user?.userData?.user?.Permissions && getUserAccessPermission("Modules", "AMS", "any");
  
  const assetSentinelPermission =
    user?.userData?.user?.Permissions && getUserAccessPermission("Modules", "Asset Sentinel");

  const hasRead = (item) => {
    if (item.alwaysVisible) return true;

    if (!item.permission || !item.permission.area || !item.permission.key) {
      return false;
    }

    const permObj = getAccessPermission(
      item.permission.area,
      item.permission.key,
      userAgency
    );
    return !!permObj?.Read;
  };

  const allowedMenu = dynamicMenuItems
    .map((item) => {
      if (item.subMenu) {
        const filteredSub = item.subMenu.filter((s) => hasRead(s));
        const parentAllowed = hasRead(item);
        return parentAllowed ? { ...item, subMenu: filteredSub } : null;
      } else {
        return hasRead(item) ? item : null;
      }
    })
    .filter(Boolean);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerCollapse = () => {
    setIsDrawerCollapsed(!isDrawerCollapsed);
  };

  const isCodeRouteAllowed = (route) => {
    if (!route) return true;
    if (route.isAMSCode === true && !amsPermission) return false;
    if (route.isAMSCode === false && !assetSentinelPermission) return false;
    return true;
  };

  const drawerContent = (
    <Box sx={{ overflow: "auto" }}>
      <List>
        {allowedMenu.map((item) => {
          const route = codeRoutes.find((r) => r.path === item.path);
          if (!isCodeRouteAllowed(route)) return null;

          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => {
                  history.push(`${path}/${item.path}`);
                  if (mobileOpen) setMobileOpen(false);
                }}
                selected={location.pathname === `${path}/${item.path}`}
                sx={{
                  justifyContent: isDrawerCollapsed ? "center" : "initial",
                  px: isDrawerCollapsed ? 1 : 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: isDrawerCollapsed ? "auto" : 56,
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!isDrawerCollapsed && <ListItemText primary={item.label} />}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better performance on mobile
        }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          width: isDrawerCollapsed ? collapsedDrawerWidth : drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: isDrawerCollapsed ? collapsedDrawerWidth : drawerWidth,
            boxSizing: "border-box",
            transition: "width 0.2s",
            height: "calc(100vh - 77px)",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <IconButton onClick={handleDrawerCollapse}>
            <MenuIcon />
          </IconButton>
        </Box>
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2 },
          width: {
            sm: `calc(100% - ${
              isDrawerCollapsed ? collapsedDrawerWidth : drawerWidth
            }px)`,
          },
          transition: "width 0.2s",
        }}
      >
        <AppBar
          position="static"
          sx={{ display: { xs: "block", sm: "none" }, mb: 2 }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap>
              Code Settings
            </Typography>
          </Toolbar>
        </AppBar>
        <Switch>
          {codeRoutes.map((route) => (
            <Route
              key={route.path}
              path={`${path}/${route.path}`}
              render={() => (
                <CodeTable
                  serviceName={route.serviceName}
                  stateName={route.stateName}
                  tableName={route.tableName}
                  columnOrderKey={route.columnOrderKey}
                  columnSizeKey={route.columnSizeKey}
                  columnVisibilityKey={route.columnVisibilityKey}
                  tableSortModelKey={route.tableSortModelKey}
                  title={route.title}
                />
              )}
            />
          ))}
          <Route path={path}>
            <div></div>
          </Route>
        </Switch>
      </Box>
    </Box>
  );
};

export default Codes;
